<template>
  <div class="app">
    <!-- Recoverable fallback shown when a descendant component throws,
         instead of leaving the user on a blank white screen. -->
    <div v-if="hasFatalError" class="d-flex flex-column align-center justify-center" style="min-height: 100vh;">
      <v-icon icon="$alert" color="error" size="64" class="mb-4" />
      <div class="text-h6 mb-2">Something went wrong</div>
      <div class="text-body-2 text-medium-emphasis mb-6">
        An unexpected error occurred. Reloading the page usually fixes this.
      </div>
      <v-btn color="primary" @click="reloadApp">Reload page</v-btn>
    </div>

    <template v-else>
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </template>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top right"
      multi-line
    >
      <div class="d-flex flex-column">
        <div v-if="snackbar.title" class="text-subtitle-1 font-weight-bold">{{ snackbar.title }}</div>
        <div>{{ snackbar.text }}</div>
      </div>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, onErrorCaptured } from 'vue';
import { RouterView } from 'vue-router';
import { useNotificationStore } from '@/stores/notification';
import { storeToRefs } from 'pinia';

const notificationStore = useNotificationStore();
const { snackbar } = storeToRefs(notificationStore);

// Fallback error boundary: catches errors from descendant components so an
// uncaught render/lifecycle error shows a recoverable screen, not a white page.
const hasFatalError = ref(false);

onErrorCaptured((err) => {
  console.error('[App onErrorCaptured]', err);
  hasFatalError.value = true;
  // Stop further propagation; the fallback UI above handles recovery.
  return false;
});

const reloadApp = (): void => {
  window.location.reload();
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
